<!-- Script -->
<script lang="ts">
  // Loadash
  import _ from "lodash";
  import type { sassNull } from "sass";

  // Props
  export let boardSize: number;

  // Snake
  let snake = new Array([]);
  $: snakeLen = snake.length;
  let food = new Array();
  let foodEaten = false;

  // Game stuff
  let direction = [0, 1];
  let gameOver = false;
  let start = true;
  let lastRenderTime: number;
  $: snakeSpeed = 2 * (1 + snakeLen / 5);
  let snakeBite = false;

  // Generate a random integer
  function getRandomInt(max: number) {
    return Math.floor(Math.random() * Math.floor(max));
  }

  // Update food position randomly
  function randomFood() {
    let foodPos = [getRandomInt(boardSize - 1), getRandomInt(boardSize - 1)];
    while (snake.some((el) => _.isEqual(el, foodPos))) {
      foodPos = [getRandomInt(boardSize - 1), getRandomInt(boardSize - 1)];
    }
    return foodPos;
  }

  // Handle the keypress events
  function handleKeydown(e: KeyboardEvent) {
    // We start or restart the game
    if (gameOver || start) {
      startGame();
      requestAnimationFrame(loop);
      return;
    }
    // Move the snake
    switch (e.key) {
      case "ArrowLeft":
        direction = [0, -1];
        break;
      case "ArrowRight":
        direction = [0, 1];
        break;
      case "ArrowUp":
        direction = [-1, 0];
        break;
      case "ArrowDown":
        direction = [1, 0];
        break;
      default:
        return;
    }
  }

  // Start the game
  function startGame() {
    gameOver = false;
    start = false;
    snake = [[Math.floor(boardSize/2), Math.floor(boardSize/2)]];
    food = randomFood();
    snakeBite = false;
  }

  function playGame() {
    // Make a copy of the snake
    let snakeTmp = [...snake];
    // Move the snake body
    for (let i = snake.length - 1; i >= 1; i--) {
      snakeTmp[i] = [snakeTmp[i - 1][0], snakeTmp[i - 1][1]];
    }
    // Move the snake head
    snakeTmp[0] = [snake[0][0] + direction[0], snake[0][1] + direction[1]];

    // Check if snake eats the food
    if (_.isEqual(snakeTmp[0], food)) {
      snakeTmp.push(food);
      foodEaten = true;
      // Add new food block on the board
      food = randomFood();
    } else {
      foodEaten = false;
    }

    // Check out of bounds
    if (
      isOutOfBounds(snakeTmp[0][0]) ||
      isOutOfBounds(snakeTmp[0][1]) ||
      snakeCollision(snakeTmp)
    ) {
      gameOver = true;
    }

    // Update the snake
    if (!gameOver) {
      snake = [...snakeTmp];
    }
  }

  // Check if the snake is out of bounds
  function isOutOfBounds(n: number) {
    return n < 0 || n > boardSize - 1;
  }

  // Check if the snake collides with itself
  function snakeCollision(sn: number[][]) {
    const stringifiedSnake = sn.map((s) => JSON.stringify(s));
    //console.log(new Set(stringifiedSnake), stringifiedSnake);
    if (
      snake.length > 1 &&
      new Set(stringifiedSnake).size != stringifiedSnake.length &&
      !foodEaten
    ) {
      snakeBite = true;
      return true;
    }
  }

  // Loop
  function loop(currentTime: number) {
    if (gameOver || start) return;
    // New animation frame
    requestAnimationFrame(loop);
    // Time elapsed
    let delta = (currentTime - lastRenderTime) / 1000;
    // Should the snake be moved?
    if (delta < 1 / snakeSpeed) return;
    // Play the game
    playGame();
    // Update rendering times
    lastRenderTime = currentTime;
  }
</script>

<!-- HTML -->
<svelte:window on:keydown={(e) => handleKeydown(e)} />
{#if !gameOver && !start}
  <div class="gameinfo len">
    <span>Snake length</span><span>{snakeLen}</span>
  </div>
{:else}
  <div class="gameinfo">The snake is waiting to start.</div>
{/if}
<div class="board">
  {#each new Array(boardSize) as row, i}
    <div class="row">
      {#each new Array(boardSize) as col, k}
        <div
          class="square"
          class:snake={snake.some((el) => _.isEqual(el, [i, k]))}
          class:food={_.isEqual(food, [i, k])}
        />
      {/each}
    </div>
  {/each}
  {#if gameOver || start}
    <div class="game-overlay">
      {#if start}
        <h2>Welcome to the snake game!</h2>
        <p>Press any key to start.</p>
      {:else}
        <h2>The game has ended!</h2>
        {#if snakeBite}
          <p class="msg">
            Your snake had a length of {snakeLen} but it bit itself.
          </p>
        {:else}
          <p class="msg">
            Your snake had a length of {snakeLen} but went out of bounds.
          </p>
        {/if}
        <p>Press any key to play again.</p>
      {/if}
    </div>
  {/if}
</div>

<!-- Style -->
<style>
  .gameinfo {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 1em;
    padding: 0.5em;
    margin-bottom: 1em;
    padding: 0.25em 0.5em;
    height: 2em;
  }

  .gameinfo.len {
    background: hsl(210, 100%, 76%);
  }

  .board {
    position: relative;
    width: 60vmin;
    aspect-ratio: 1;
    display: grid;
    grid-template-rows: repeat(var(--board-size), 1fr);
    background-color: hsl(0, 0%, 92%);
    border: 0.5vmin solid hsl(0, 0%, 85%);
  }

  .game-overlay {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    position: absolute;
    inset: -0.5vmin;
    color: rgba(255, 255, 255, 0.9);
    background-color: rgba(0, 0, 0, 0.4);
    z-index: 99;
  }

  .row {
    display: grid;
    grid-template-columns: repeat(var(--board-size), 1fr);
  }

  .square {
    aspect-ratio: 1;
  }

  .snake {
    background-color: hsl(210, 90%, 62%);
    border: 0.25vmin solid rgba(0, 0, 0, 0.25);
  }

  .food {
    background-color: hsl(33, 80%, 60%);
    border: 0.25vmin solid rgba(0, 0, 0, 0.25);
  }

  .msg {
    padding: 0.25em 0.5em;
    max-width: 80%;
    margin: 0 auto;
    background: linear-gradient(90deg, firebrick, dodgerblue);
    color: inherit;
    border-radius: 4px;
  }
</style>
